import DynamicIcon from "./DynamicIcon";

/* The raw category record as /product/all returns it. */
export type RawCategory = {
  id: string;
  title?: string;
  categoryType: string;
  iconName?: string;
  createdAt: any;
  parentIds?: string[];
};

export const CategoryObj = (categoryData: {
  id: any;
  title: any;
  categoryType: any;
  iconName: any;
  createdAt: any;
  parentIds?: string[];
}) => {
  return {
    id: categoryData.id,
    name: categoryData.title,
    urlParam: categoryData.categoryType,
    icon: <DynamicIcon name={categoryData.iconName} />,
    createdAt: categoryData.createdAt,
    parentIds: categoryData.parentIds || [],
  };
};

/*
 * Category roll-up.
 *
 * One restaurant ("Cafe Kulachi") owns nine sub-categories, which put ten pills
 * in the filter bar for a single vendor. The children are hidden and their
 * products surface under the parent instead, so nothing becomes unreachable —
 * the parent's own products and its descendants' are shown together.
 */

/** True when this category is nested under another category that is present. */
export const isChildOf = (categories: RawCategory[], category: RawCategory) => {
  const parents = category?.parentIds || [];
  if (!parents.length) return false;
  // An orphan — parent hidden or deleted — is treated as a root, otherwise its
  // products would drop out of the menu entirely.
  return parents.some((pid) => categories.some((c) => c.id === pid));
};

/** Every categoryType matched when filtering by `rootType`: itself + descendants. */
export const descendantTypes = (
  categories: RawCategory[],
  rootType: string
): Set<string> => {
  const types = new Set<string>([rootType]);
  const root = (categories || []).find((c) => c.categoryType === rootType);
  if (!root) return types;

  let frontier = [root.id];
  // Depth is 1 today; the loop keeps it correct if the tree ever deepens. The
  // seen-set stops a mis-entered cycle from hanging the render.
  const seen = new Set<string>(frontier);
  while (frontier.length) {
    // Captured per-iteration so the closure below does not read a mutating
    // outer binding, and membership is O(1) rather than a scan per parent.
    const current = new Set(frontier);
    const next: string[] = [];
    for (const c of categories || []) {
      if (seen.has(c.id)) continue;
      if ((c.parentIds || []).some((pid) => current.has(pid))) {
        types.add(c.categoryType);
        next.push(c.id);
        seen.add(c.id);
      }
    }
    frontier = next;
  }
  return types;
};

/** The category carrying this categoryType, if any. */
export const findByType = (categories: RawCategory[], type: string) =>
  (categories || []).find((c) => c.categoryType === type);

/** Direct children of a category id. */
export const childrenOf = (categories: RawCategory[], parentId: string) =>
  (categories || []).filter((c) => (c.parentIds || []).includes(parentId));

/*
 * Which root the filter bar should treat as "open".
 *
 * The active filter can be a root or one of its children; both need to keep the
 * same sub-row on screen, otherwise picking a child would collapse the very
 * list it was picked from.
 */
export const resolveActiveRoot = (
  categories: RawCategory[],
  filter: string
): RawCategory | null => {
  if (!filter || filter === "all") return null;
  const current = findByType(categories, filter);
  if (!current) return null;
  const parentId = (current.parentIds || []).find((pid) =>
    (categories || []).some((c) => c.id === pid)
  );
  return parentId
    ? (categories || []).find((c) => c.id === parentId) || current
    : current;
};
